import { NgIf, NgClass, NgStyle } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { interval, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import { FuseConfigService, Scheme } from '@fuse/services/config';
import { Store } from '@ngrx/store';
import { AppState } from 'app/store/app.reducer';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { NotificationsService } from 'app/services/notificaciones.service';
import { GetNotificationsData, UptimeKumaData } from 'app/interfaces/notificaciones.interface';
import { notifications } from 'app/mock-api/common/notifications/data';
import { StatusBarComponent } from 'app/modules/admin/status-bar/status-bar.component';

@Component({
    selector     : 'classic-layout',
    templateUrl  : './classic.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports: [FuseLoadingBarComponent, FuseVerticalNavigationComponent, MatButtonModule, MatIconModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, NotificationsComponent, UserComponent, NgIf, RouterOutlet, QuickChatComponent, MatMenuModule, MatDividerModule, NgClass, NgStyle, StatusBarComponent],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    environment= environment;
    isDarkMode: boolean = environment.scheme === 'dark';
    userEmail: string;
    showHeaderLogo: boolean = false;
    version: string = environment.version;
    // mensajes: any[] = [];
    hasAlerts: boolean = false;
    private sub!: Subscription;
    userRole: string;
    userGroup: string;
    userId: number | string;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _notificationsService: NotificationsService,
        private _fuseConfigService: FuseConfigService,
        private _store: Store<AppState>
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) =>
            {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
        this._store.select('auth').subscribe((auth) => {
            this.userEmail = auth.auth.userEmail;
            this.userRole = auth.auth.userRole;
            this.userGroup = auth.auth.userGroup;
            this.userId = auth.auth.userId;
        });

        this.sub = this._notificationsService.connect(environment.notificationToken).subscribe((msg: UptimeKumaData) => {
            if(msg.message.toLowerCase().includes('down')) {
                this.hasAlerts = true;
            }
            if(msg.message.toLowerCase().includes('up')) {
                this.hasAlerts = false;
            }
            // this.mensajes.push(msg);
        });

        // this._notificationsService.getMessages('CNIF9vd9gJ3Wq5k').subscribe((notifications: GetNotificationsData) => {
        //     const downNotifications = notifications.messages.filter((notificationMessage: UptimeKumaData) => notificationMessage.message.toLowerCase().includes('down]'));
        //     const upNotifications = notifications.messages.filter((notificationMessage: UptimeKumaData) => notificationMessage.message.toLowerCase().includes('up]'));
        //     if(downNotifications.length > upNotifications.length) this.hasAlerts = true;
        // })
        this._notificationsService.getServersStatus().subscribe((status: string) => {
            const dividedArray: string[] = status.split('title>');
            if(dividedArray[1].toLowerCase().includes('up')) {
                this.hasAlerts = false;
            } else {
                this.hasAlerts = true;
            }
        })
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.sub.unsubscribe();
        this._notificationsService.disconnect();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            if(navigation.opened) {
                this.showHeaderLogo = true;
            } else {
                this.showHeaderLogo = false;
            }
            // Toggle the opened status
            navigation.toggle();
        }
    }
    signOut() {
        console.log('signOut desde material');
        this._authService.signOut();
        this._router.navigate(['/sign-in']);
    }

    checkStatus(){
        window.open('https://uptime.puntosalud.ar/status/servicios', '_blank')
    }

    switchScheme(): void {
        this.isDarkMode = !this.isDarkMode;
        environment.scheme = this.isDarkMode ? 'dark' : 'light';
        this._fuseConfigService.config = {
            scheme: environment.scheme as Scheme,
        };
    }

    seeProfileData(){
        this._router.navigate([`/usuarios/mi-usuario/${this.userId}`]);
    }
}
